/**
 * Compose flux caméra + canvas WebGL dans un canvas 2D hors écran,
 * puis capture ce canvas via captureStream() + MediaRecorder.
 *
 * C'est aujourd'hui la seule approche 100 % web pour obtenir
 * caméra ET AR dans la même vidéo, sans service commercial.
 */

export interface CompositeRecorderOptions {
  maxWidth: number;
  fps: number;
  mimeType: string;
}

export class CompositeCanvasRecorder {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private rafId = 0;
  private drawing = false;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly glCanvas: HTMLCanvasElement,
    private readonly options: CompositeRecorderOptions,
  ) {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponible');
    this.ctx = ctx;
  }

  start(): void {
    this.resizeToVideo();
    this.drawing = true;
    this.drawLoop();

    const stream = this.canvas.captureStream(this.options.fps);
    this.chunks = [];
    this.recorder = new MediaRecorder(stream, {
      mimeType: this.options.mimeType,
      videoBitsPerSecond: 5_000_000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start(1000); // timeslice : limite la perte si crash
  }

  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const recorder = this.recorder;
      this.drawing = false;
      cancelAnimationFrame(this.rafId);
      if (!recorder) {
        reject(new Error("Aucun enregistrement en cours"));
        return;
      }
      recorder.onstop = () => {
        this.recorder = null;
        resolve(new Blob(this.chunks, { type: this.options.mimeType.split(';')[0] }));
      };
      recorder.onerror = () => reject(new Error("Échec de l'enregistrement"));
      recorder.stop();
    });
  }

  /** Capture une seule frame composite (fallback "photo souvenir"). */
  async captureFrame(): Promise<Blob> {
    this.resizeToVideo();
    this.drawFrame();
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Capture impossible'))),
        'image/png',
      );
    });
  }

  private resizeToVideo(): void {
    const vw = this.video.videoWidth || 720;
    const vh = this.video.videoHeight || 1280;
    const scale = Math.min(1, this.options.maxWidth / vw);
    this.canvas.width = Math.round(vw * scale);
    this.canvas.height = Math.round(vh * scale);
  }

  private drawLoop = (): void => {
    if (!this.drawing) return;
    this.drawFrame();
    this.rafId = requestAnimationFrame(this.drawLoop);
  };

  private drawFrame(): void {
    const { width, height } = this.canvas;
    // 1. flux caméra (le <video> MindAR couvre déjà l'écran, on le dessine plein cadre)
    this.ctx.drawImage(this.video, 0, 0, width, height);
    // 2. scène AR par-dessus (canvas WebGL transparent)
    this.ctx.drawImage(this.glCanvas, 0, 0, width, height);
  }
}
